import { CheckCircle2, Circle, FileText } from "lucide-react";
import { clientsApi } from "@/api/clients";
import { Badge } from "@/components/common";
import type { RodoStatus } from "@/types/clients";

export function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const METHOD_LABEL: Record<string, string> = {
  SmsCodes: "kody",
  Link: "link — klient",
  Paper: "papier",
};

/** The "ptaszek": verified/not verified, with date, method and clause version. */
export function RodoVerifiedMark({
  rodo,
  canDownloadScan = false,
}: {
  rodo: RodoStatus;
  /** Only administrators may download the scan of a paper consent. */
  canDownloadScan?: boolean;
}) {
  if (!rodo.verified || !rodo.verifiedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-faint">
        <Circle size={16} aria-hidden />
        Niezweryfikowane
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col">
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 dark:text-green-400">
        <CheckCircle2 size={16} aria-hidden />
        RODO zweryfikowane
      </span>
      {rodo.method === "Paper" ? (
        <span className="flex flex-wrap items-center gap-1 text-xs text-faint">
          <Badge variant="info">
            <FileText size={11} aria-hidden className="mr-0.5 inline" />
            Papier
          </Badge>
          podpisano {new Date(rodo.verifiedAt).toLocaleDateString("pl-PL")}
          {canDownloadScan && rodo.requestId && (
            <>
              {" · "}
              <a
                href={clientsApi.consentDocumentUrl(rodo.requestId)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 text-adara-600 underline hover:text-adara-800"
              >
                <FileText size={12} aria-hidden />
                pobierz skan
              </a>
            </>
          )}
        </span>
      ) : (
        <span className="text-xs text-faint">
          {formatDateTime(rodo.verifiedAt)} ·{" "}
          {METHOD_LABEL[rodo.method ?? ""] ?? rodo.method} · wersja{" "}
          {rodo.clauseVersion}
        </span>
      )}
    </span>
  );
}

function ConsentBadge({ label, granted }: { label: string; granted: boolean }) {
  return (
    <Badge variant={granted ? "success" : "default"}>
      {label}: {granted ? "TAK" : "NIE"}
    </Badge>
  );
}

/** Per-consent status — a verified client may still have refused marketing. */
export function ConsentBadges({ rodo }: { rodo: RodoStatus }) {
  if (!rodo.verified) {
    return <span className="text-sm text-faint">—</span>;
  }
  const marketing =
    rodo.marketingPhone || rodo.marketingSms || rodo.marketingEmail;
  return (
    <div className="flex flex-wrap gap-1.5">
      <ConsentBadge label="Baza" granted={rodo.clientBase} />
      <ConsentBadge label="Marketing" granted={marketing} />
      {rodo.health !== null && (
        <ConsentBadge label="Zdrowie" granted={rodo.health} />
      )}
    </div>
  );
}
